// Phase A — Colonies & Rooms admin.
//
// Tree-style layout: left panel lists colonies; right panel shows the
// rooms in the selected colony, with bed-detail expansion. Admin can
// CRUD at all three levels. Used pre-rollout to set up the mill's
// accommodation master before employees are linked (Phase B).

import { useMemo, useState } from 'react'
import {
  App, Badge, Button, Card, Form, Input, InputNumber, Modal, Popconfirm,
  Select, Space, Switch, Table, Tag, Typography,
} from 'antd'
import {
  ApartmentOutlined, DeleteOutlined, EditOutlined, HomeOutlined,
  PlusOutlined,
} from '@ant-design/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import {
  welfareMastersApi, type Bed, type Colony, type Room,
} from '@/features/hr/welfareMastersApi'
import PageHeader from '@/shared/components/PageHeader'
import { usePermission } from '@/shared/hooks/usePermission'
import { usePlantId } from '@/shared/hooks/usePlantId'

const { Text, Title } = Typography


// ─── Page shell ───────────────────────────────────────────────────────

export default function ColoniesRoomsPage() {
  const [selectedColony, setSelectedColony] = useState<string | null>(null)

  return (
    <div>
      <PageHeader
        title="Colonies & Rooms"
        subtitle="Mill accommodation master. Set up colonies, rooms, and beds before linking employees (Phase B)."
      />
      <div style={{ display: 'flex', gap: 16, marginTop: 16, flexWrap: 'wrap' }}>
        <div style={{ flex: '0 0 360px', minWidth: 320 }}>
          <ColoniesPanel
            selected={selectedColony}
            onSelect={setSelectedColony}
          />
        </div>
        <div style={{ flex: 1, minWidth: 480 }}>
          {selectedColony ? (
            <RoomsPanel colonyId={selectedColony} />
          ) : (
            <Card>
              <Text type="secondary">
                Select a colony on the left to view + manage its rooms.
              </Text>
            </Card>
          )}
        </div>
      </div>
    </div>
  )
}


// ─── Colonies panel (left) ────────────────────────────────────────────

function ColoniesPanel({
  selected, onSelect,
}: {
  selected: string | null
  onSelect: (id: string) => void
}) {
  const { message } = App.useApp()
  const qc = useQueryClient()
  const canEdit = usePermission('employee.create')
  const { plantId } = usePlantId()
  const [createOpen, setCreateOpen] = useState(false)
  const [editing, setEditing] = useState<Colony | null>(null)

  const { data: colonies, isLoading } = useQuery({
    queryKey: ['colonies', plantId],
    queryFn: () => welfareMastersApi.listColonies({
      plant_id: plantId ?? undefined,
    }),
    enabled: !!plantId,
  })

  const invalidate = () => qc.invalidateQueries({ queryKey: ['colonies'] })

  const createMut = useMutation({
    mutationFn: welfareMastersApi.createColony,
    onSuccess: () => { message.success('Colony added'); invalidate(); setCreateOpen(false) },
    onError: errToast(message),
  })
  const patchMut = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Record<string, unknown> }) =>
      welfareMastersApi.patchColony(id, body),
    onSuccess: () => { message.success('Saved'); invalidate(); setEditing(null) },
    onError: errToast(message),
  })
  const delMut = useMutation({
    mutationFn: welfareMastersApi.deleteColony,
    onSuccess: () => { message.success('Deleted'); invalidate() },
    onError: errToast(message),
  })

  return (
    <Card
      title={<Space><ApartmentOutlined /> Colonies</Space>}
      extra={canEdit && (
        <Button type="primary" size="small" icon={<PlusOutlined />}
          onClick={() => setCreateOpen(true)}>
          Add
        </Button>
      )}
    >
      <Table
        rowKey="id"
        size="small"
        loading={isLoading}
        pagination={false}
        showHeader={false}
        rowClassName={(r) => r.id === selected ? 'ant-table-row-selected' : ''}
        onRow={(r) => ({
          onClick: () => onSelect(r.id),
          style: { cursor: 'pointer' },
        })}
        columns={[
          {
            title: '', key: 'col',
            render: (_: unknown, r: Colony) => (
              <div>
                <Space size={6}>
                  <Text strong>{r.colony_name}</Text>
                  <Text code style={{ fontSize: 10 }}>{r.colony_code}</Text>
                  {!r.is_active && <Tag color="default">INACTIVE</Tag>}
                </Space>
                <div style={{ fontSize: 11, color: '#888' }}>
                  {r.total_rooms} room(s) · {r.ownership_type.replace('_', ' ')}
                </div>
              </div>
            ),
          },
          ...(canEdit ? [{
            title: '', key: 'act', width: 80,
            render: (_: unknown, r: Colony) => (
              <Space size={0}>
                <Button size="small" type="text" icon={<EditOutlined />}
                  onClick={(e) => { e.stopPropagation(); setEditing(r) }} />
                <Popconfirm title="Delete this colony?"
                  description="Only works if no rooms exist."
                  onConfirm={() => delMut.mutate(r.id)}>
                  <Button size="small" type="text" danger icon={<DeleteOutlined />}
                    onClick={(e) => e.stopPropagation()} />
                </Popconfirm>
              </Space>
            ),
          }] : []),
        ] as never}
        dataSource={colonies ?? []}
      />
      <ColonyFormModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSubmit={(body) => createMut.mutate({ ...body, plant_id: plantId })}
        submitting={createMut.isPending}
        plantId={plantId}
      />
      <ColonyFormModal
        open={!!editing}
        colony={editing}
        onClose={() => setEditing(null)}
        onSubmit={(body) => editing && patchMut.mutate({ id: editing.id, body })}
        submitting={patchMut.isPending}
        plantId={plantId}
      />
    </Card>
  )
}


function ColonyFormModal({
  open, colony, onClose, onSubmit, submitting, plantId,
}: {
  open: boolean
  colony?: Colony | null
  onClose: () => void
  onSubmit: (body: Record<string, unknown>) => void
  submitting: boolean
  plantId: string | null
}) {
  const [form] = Form.useForm()
  const isEdit = !!colony
  return (
    <Modal
      open={open} onCancel={onClose} destroyOnClose
      title={isEdit ? `Edit ${colony?.colony_name}` : 'Add Colony'}
      okText={isEdit ? 'Save' : 'Add'} confirmLoading={submitting} width={480}
      onOk={() => form.validateFields().then(onSubmit)}
    >
      <Form form={form} layout="vertical" initialValues={colony ?? {
        ownership_type: 'COMPANY_OWNED',
      }} disabled={!plantId && !isEdit}>
        <Form.Item name="colony_code" label="Colony Code"
          rules={[{ required: true, min: 2, max: 30 }]}
          tooltip="Short code, e.g. COL-A. Cannot be changed after create.">
          <Input placeholder="COL-A" disabled={isEdit} />
        </Form.Item>
        <Form.Item name="colony_name" label="Colony Name"
          rules={[{ required: true }]}>
          <Input placeholder="Main Worker Colony" />
        </Form.Item>
        <Form.Item name="location" label="Location">
          <Input placeholder="Near west gate, plot 3" />
        </Form.Item>
        <Form.Item name="ownership_type" label="Ownership"
          rules={[{ required: true }]}>
          <Select options={[
            { label: 'Company-owned', value: 'COMPANY_OWNED' },
            { label: 'Rented', value: 'RENTED' },
            { label: 'Third-party', value: 'THIRD_PARTY' },
          ]} />
        </Form.Item>
        {isEdit && (
          <Form.Item name="is_active" label="Active" valuePropName="checked">
            <Switch />
          </Form.Item>
        )}
        <Form.Item name="remarks" label="Remarks">
          <Input.TextArea rows={2} />
        </Form.Item>
      </Form>
    </Modal>
  )
}


// ─── Rooms panel (right) ──────────────────────────────────────────────

function RoomsPanel({ colonyId }: { colonyId: string }) {
  const { message } = App.useApp()
  const qc = useQueryClient()
  const canEdit = usePermission('employee.create')
  const [createOpen, setCreateOpen] = useState(false)
  const [editing, setEditing] = useState<Room | null>(null)

  const { data: rooms, isLoading } = useQuery({
    queryKey: ['rooms', colonyId],
    queryFn: () => welfareMastersApi.listRooms({ colony_id: colonyId }),
  })

  const invalidate = () => qc.invalidateQueries({ queryKey: ['rooms', colonyId] })

  const createMut = useMutation({
    mutationFn: welfareMastersApi.createRoom,
    onSuccess: () => { message.success('Room added'); invalidate(); setCreateOpen(false) },
    onError: errToast(message),
  })
  const patchMut = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Record<string, unknown> }) =>
      welfareMastersApi.patchRoom(id, body),
    onSuccess: () => { message.success('Saved'); invalidate(); setEditing(null) },
    onError: errToast(message),
  })
  const delMut = useMutation({
    mutationFn: welfareMastersApi.deleteRoom,
    onSuccess: () => { message.success('Deleted'); invalidate() },
    onError: errToast(message),
  })

  return (
    <Card
      title={<Space><HomeOutlined /> Rooms ({rooms?.length ?? 0})</Space>}
      extra={canEdit && (
        <Button type="primary" size="small" icon={<PlusOutlined />}
          onClick={() => setCreateOpen(true)}>
          Add Room
        </Button>
      )}
    >
      <Table
        rowKey="id"
        size="small"
        loading={isLoading}
        pagination={{ pageSize: 25 }}
        expandable={{
          expandedRowRender: (r) => <BedsRow room={r} canEdit={canEdit} />,
          rowExpandable: () => true,
        }}
        columns={[
          {
            title: 'Room', key: 'room',
            render: (_: unknown, r: Room) => (
              <Space size={4} direction="vertical">
                <Text strong>
                  {r.building_block ? `${r.building_block} / ` : ''}{r.room_no}
                </Text>
                {r.floor && (
                  <Text type="secondary" style={{ fontSize: 11 }}>Floor {r.floor}</Text>
                )}
              </Space>
            ),
          },
          {
            title: 'Type', dataIndex: 'room_type', key: 't', width: 130,
            render: (v: string) => <Tag>{v.replace('_', ' ')}</Tag>,
          },
          {
            title: 'Cap', dataIndex: 'capacity', key: 'c', width: 60,
            render: (v: number) => <Tag color="blue">{v}</Tag>,
          },
          {
            title: 'Status', dataIndex: 'status', key: 'st', width: 110,
            render: (v: string) => statusTag(v),
          },
          {
            title: 'Rent ₹/mo', dataIndex: 'monthly_rent', key: 'r', width: 100,
            render: (v: string) => `₹${Number(v).toLocaleString('en-IN')}`,
          },
          ...(canEdit ? [{
            title: '', key: 'act', width: 80,
            render: (_: unknown, r: Room) => (
              <Space size={0}>
                <Button size="small" type="text" icon={<EditOutlined />}
                  onClick={() => setEditing(r)} />
                <Popconfirm title="Delete this room?"
                  description="Only works if no beds occupied."
                  onConfirm={() => delMut.mutate(r.id)}>
                  <Button size="small" type="text" danger icon={<DeleteOutlined />} />
                </Popconfirm>
              </Space>
            ),
          }] : []),
        ] as never}
        dataSource={rooms ?? []}
      />
      <RoomFormModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSubmit={(body) => createMut.mutate({ ...body, colony_id: colonyId })}
        submitting={createMut.isPending}
      />
      <RoomFormModal
        open={!!editing}
        room={editing}
        onClose={() => setEditing(null)}
        onSubmit={(body) => editing && patchMut.mutate({ id: editing.id, body })}
        submitting={patchMut.isPending}
      />
    </Card>
  )
}


function RoomFormModal({
  open, room, onClose, onSubmit, submitting,
}: {
  open: boolean
  room?: Room | null
  onClose: () => void
  onSubmit: (body: Record<string, unknown>) => void
  submitting: boolean
}) {
  const [form] = Form.useForm()
  const isEdit = !!room
  return (
    <Modal
      open={open} onCancel={onClose} destroyOnClose
      title={isEdit ? `Edit ${room?.room_no}` : 'Add Room'}
      okText={isEdit ? 'Save' : 'Add'} confirmLoading={submitting} width={600}
      onOk={() => form.validateFields().then(onSubmit)}
    >
      <Form form={form} layout="vertical" initialValues={room ? {
        ...room,
        monthly_rent: Number(room.monthly_rent),
      } : {
        room_type: 'WORKER_ROOM',
        capacity: 1,
        electricity_rule: 'FREE',
        water_rule: 'FREE',
        maintenance_rule: 'FREE',
        monthly_rent: 0,
      }}>
        <Space.Compact block>
          <Form.Item name="building_block" label="Block" style={{ flex: 1, marginRight: 8 }}>
            <Input placeholder="Block A" />
          </Form.Item>
          <Form.Item name="floor" label="Floor" style={{ flex: 1, marginRight: 8 }}>
            <Input placeholder="Ground / 1 / 2" />
          </Form.Item>
          <Form.Item name="room_no" label="Room No."
            rules={[{ required: true }]} style={{ flex: 1 }}>
            <Input placeholder="101" />
          </Form.Item>
        </Space.Compact>
        <Space.Compact block>
          <Form.Item name="room_type" label="Room Type"
            rules={[{ required: true }]} style={{ flex: 1, marginRight: 8 }}>
            <Select options={[
              { label: 'Worker Room', value: 'WORKER_ROOM' },
              { label: 'Staff Room', value: 'STAFF_ROOM' },
              { label: 'Family Quarter', value: 'FAMILY_QUARTER' },
              { label: 'Dormitory', value: 'DORMITORY' },
              { label: 'Guest Room', value: 'GUEST_ROOM' },
            ]} />
          </Form.Item>
          <Form.Item name="capacity" label="Capacity"
            rules={[{ required: true }]} style={{ flex: 1, marginRight: 8 }}>
            <InputNumber min={1} max={20} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="allowed_gender" label="Gender" style={{ flex: 1 }}>
            <Select allowClear placeholder="any" options={[
              { label: 'Male', value: 'MALE' },
              { label: 'Female', value: 'FEMALE' },
              { label: 'Any', value: 'ANY' },
            ]} />
          </Form.Item>
        </Space.Compact>
        <Form.Item name="monthly_rent" label="Monthly Rent (₹)">
          <InputNumber min={0} step={50} style={{ width: '100%' }} />
        </Form.Item>
        <Space.Compact block>
          <Form.Item name="electricity_rule" label="Electricity" style={{ flex: 1, marginRight: 8 }}>
            <Select options={[
              { label: 'Free', value: 'FREE' },
              { label: 'Flat rate', value: 'FLAT_RATE' },
              { label: 'Metered', value: 'METERED' },
            ]} />
          </Form.Item>
          <Form.Item name="water_rule" label="Water" style={{ flex: 1, marginRight: 8 }}>
            <Select options={[
              { label: 'Free', value: 'FREE' },
              { label: 'Flat rate', value: 'FLAT_RATE' },
            ]} />
          </Form.Item>
          <Form.Item name="maintenance_rule" label="Maintenance" style={{ flex: 1 }}>
            <Select options={[
              { label: 'Free', value: 'FREE' },
              { label: 'Flat rate', value: 'FLAT_RATE' },
            ]} />
          </Form.Item>
        </Space.Compact>
        {isEdit && (
          <Space.Compact block>
            <Form.Item name="status" label="Status" style={{ flex: 1, marginRight: 8 }}>
              <Select options={[
                { label: 'Available', value: 'AVAILABLE' },
                { label: 'Occupied', value: 'OCCUPIED' },
                { label: 'Partially Occupied', value: 'PARTIALLY_OCCUPIED' },
                { label: 'Under Maintenance', value: 'UNDER_MAINTENANCE' },
                { label: 'Blocked', value: 'BLOCKED' },
              ]} />
            </Form.Item>
            <Form.Item name="is_active" label="Active" valuePropName="checked" style={{ flex: 1 }}>
              <Switch />
            </Form.Item>
          </Space.Compact>
        )}
        <Form.Item name="remarks" label="Remarks">
          <Input.TextArea rows={2} />
        </Form.Item>
      </Form>
    </Modal>
  )
}


// ─── Beds row (expand) ────────────────────────────────────────────────

function BedsRow({ room, canEdit }: { room: Room; canEdit: boolean }) {
  const { message } = App.useApp()
  const qc = useQueryClient()
  const [adding, setAdding] = useState(false)
  const [newBedNo, setNewBedNo] = useState('')

  const { data: beds, isLoading } = useQuery({
    queryKey: ['beds', room.id],
    queryFn: () => welfareMastersApi.listBeds({ room_id: room.id }),
  })

  const invalidate = () => qc.invalidateQueries({ queryKey: ['beds', room.id] })

  const addMut = useMutation({
    mutationFn: welfareMastersApi.createBed,
    onSuccess: () => { message.success('Bed added'); invalidate(); setAdding(false); setNewBedNo('') },
    onError: errToast(message),
  })
  const delMut = useMutation({
    mutationFn: welfareMastersApi.deleteBed,
    onSuccess: () => { message.success('Removed'); invalidate() },
    onError: errToast(message),
  })

  const occupied = useMemo(
    () => (beds ?? []).filter((b) => b.status === 'OCCUPIED').length,
    [beds],
  )
  const canAdd = !beds || beds.length < room.capacity

  return (
    <div style={{ background: '#fafafa', padding: 12, borderRadius: 4 }}>
      <Space wrap style={{ marginBottom: 8 }}>
        <Text strong style={{ fontSize: 12 }}>Beds:</Text>
        <Badge count={`${occupied}/${room.capacity}`} style={{ background: '#1677ff' }} />
        {canEdit && canAdd && (adding ? (
          <Space.Compact>
            <Input size="small" value={newBedNo}
              onChange={(e) => setNewBedNo(e.target.value)}
              placeholder="Bed no (e.g. 1)" style={{ width: 120 }}
              onPressEnter={() => newBedNo.trim() &&
                addMut.mutate({ room_id: room.id, bed_no: newBedNo.trim() })}
            />
            <Button size="small" type="primary"
              onClick={() => newBedNo.trim() &&
                addMut.mutate({ room_id: room.id, bed_no: newBedNo.trim() })}
              loading={addMut.isPending}>Add</Button>
            <Button size="small" onClick={() => { setAdding(false); setNewBedNo('') }}>
              Cancel
            </Button>
          </Space.Compact>
        ) : (
          <Button size="small" type="dashed" icon={<PlusOutlined />}
            onClick={() => setAdding(true)}>Add bed</Button>
        ))}
        {canEdit && !canAdd && (
          <Text type="secondary" style={{ fontSize: 11 }}>(at capacity)</Text>
        )}
      </Space>
      {isLoading ? <Text type="secondary">Loading beds…</Text> : (
        <Space wrap>
          {(beds ?? []).length === 0 && (
            <Text type="secondary" style={{ fontSize: 11 }}>
              No beds yet. Add beds so workers can be allocated (Phase B).
            </Text>
          )}
          {(beds ?? []).map((b) => (
            <BedChip key={b.id} bed={b} canEdit={canEdit}
              onDelete={() => delMut.mutate(b.id)} />
          ))}
        </Space>
      )}
    </div>
  )
}


function BedChip({ bed, canEdit, onDelete }: {
  bed: Bed; canEdit: boolean; onDelete: () => void
}) {
  const color = bed.status === 'OCCUPIED' ? 'red'
    : bed.status === 'BLOCKED' ? 'default' : 'green'
  return (
    <Tag color={color} style={{ padding: '4px 8px' }}>
      <Space size={4}>
        <Text strong style={{ fontSize: 12 }}>Bed {bed.bed_no}</Text>
        <Text style={{ fontSize: 10, opacity: 0.7 }}>{bed.status}</Text>
        {canEdit && bed.status !== 'OCCUPIED' && (
          <Popconfirm title="Remove this bed?" onConfirm={onDelete}>
            <DeleteOutlined style={{ fontSize: 10, cursor: 'pointer' }} />
          </Popconfirm>
        )}
      </Space>
    </Tag>
  )
}


// ─── Helpers ──────────────────────────────────────────────────────────

function statusTag(status: string) {
  const map: Record<string, string> = {
    AVAILABLE: 'green',
    OCCUPIED: 'red',
    PARTIALLY_OCCUPIED: 'gold',
    UNDER_MAINTENANCE: 'orange',
    BLOCKED: 'default',
  }
  return <Tag color={map[status] ?? 'default'}>{status.replace('_', ' ')}</Tag>
}

function errToast(message: ReturnType<typeof App.useApp>['message']) {
  return (e: unknown) => {
    const err = e as { response?: { data?: { detail?: string } } }
    message.error(err?.response?.data?.detail ?? 'Save failed')
  }
}
